import { useState, useEffect } from 'react';
import { useCodeMirror } from '@uiw/react-codemirror';
import { yaml as cmYaml } from '@codemirror/lang-yaml';
import { json as cmJson } from '@codemirror/lang-json';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import ErrorOutlineRoundedIcon from '@mui/icons-material/ErrorOutlineRounded';
import CircularProgress from '@mui/material/CircularProgress';
import {
  ConfigApi,
  configApiRef,
  IdentityApi,
  identityApiRef,
  useApi,
} from '@backstage/core-plugin-api';
import CheckCircleOutlineRoundedIcon from '@material-ui/icons/CheckCircleOutlineRounded';
import { makeStyles } from '@material-ui/core/styles';
import { useTheme } from '@material-ui/core/styles';
import { handbookRules } from '../utils/handbookLinterRules';
import * as pako from 'pako';

let config: ConfigApi;
let identity: IdentityApi;

const useStyles = makeStyles({
  headerContainer: {
    width: '100%',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '10px 20px',
  },
  spectralContainer: {
    height: 'auto',
    display: 'flex',
    flexDirection: 'column',
  },
  editorContainer: {
    padding: '20px',
    borderBottom: '2px solid #ddd',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'left',
    width: '100%',
    position: 'relative',
    marginTop: '-10px',
  },
  runLinterButton: {
    marginTop: '10px',
    padding: '5px 10px',
    marginLeft: '300px',
    marginRight: '10px',
    fontSize: '14px',
    fontWeight: 'bold',
    backgroundColor: '#007bff',
    color: 'white',
    border: 'none',
    borderRadius: '5px',
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: '#0056b3',
    },
  },
  bottomContainer: {
    display: 'flex',
    marginTop: '20px',
  },
  sectionSelector: {
    display: 'flex',
    flexDirection: 'column',
    width: '200px',
  },
  sectionButton: {
    cursor: 'pointer',
    fontWeight: 'bold',
    border: 'none',
    backgroundColor: (props: { isSelected: boolean }) =>
      props.isSelected ? '#ddd' : '#f4f4f4',
    borderRadius: '5px',
    marginBottom: '5px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '10px 15px',
    '&:hover': {
      backgroundColor: '#ddd',
    },
  },
  selectedButton: {
    backgroundColor: '#ddd !important',
  },
  issueCount: {
    backgroundColor: '#dc3545',
    color: 'white',
    fontSize: '14px',
    fontWeight: 'bold',
    borderRadius: '12px',
    padding: '3px 8px',
  },
  contentArea: {
    flex: 1,
    padding: '20px',
  },
});

const severityStyles: Record<
  number,
  (isDark: boolean) => { label: string; color: string; icon: JSX.Element }
> = {
  0: isDark => ({
    label: 'Error',
    color: isDark ? '#5f1a1a' : '#f8d7da',
    icon: <ErrorOutlineRoundedIcon style={{ color: 'red' }} />,
  }),
  1: isDark => ({
    label: 'Warning',
    color: isDark ? '#665c00' : '#fff3cd',
    icon: <WarningAmberRoundedIcon style={{ color: 'red' }} />,
  }),
  2: isDark => ({
    label: 'Info',
    color: isDark ? '#164e63' : '#d1ecf1',
    icon: <InfoOutlinedIcon style={{ color: 'blue' }} />,
  }),
  3: isDark => ({
    label: 'Hint',
    color: isDark ? '#3a3b3c' : '#e2e3e5',
    icon: <InfoOutlinedIcon style={{ color: 'gray' }} />,
  }),
};
export function EmbeddedSpectralLinter() {
  const classes = useStyles({ isSelected: false });
  const theme = useTheme();
  const isDark = theme.palette.type === 'dark';
  config = useApi(configApiRef);
  identity = useApi(identityApiRef);
  const [editorContent, setEditorContent] = useState<string>('');
  const [selectedFormat, setSelectedFormat] = useState<'yaml' | 'json'>('yaml');
  const [lintResults, setLintResults] = useState<{ [key: string]: any[] }>({});
  const [selectedSection, setSelectedSection] = useState<string>('oas_rules'); // Default to "OpenAPI rules"
  const [isLintingDone, setIsLintingDone] = useState<boolean>(false); // To track if linting is done
  const [isLinting, setIsLinting] = useState<boolean>(false); // Track if linting is in progress

  const { view: editorView, setContainer } = useCodeMirror({
    value: '',
    extensions: [selectedFormat === 'json' ? cmJson() : cmYaml()],
    height: '400px',
    theme: isDark ? 'dark' : 'light',
    onChange: (value: string) => {
      setEditorContent(value);
      try {
        JSON.parse(value);
        setSelectedFormat('json');
      } catch {
        setSelectedFormat('yaml');
      }
    },
  });
  useEffect(() => {
    const editorContainer = document.getElementById(
      'editor',
    ) as HTMLDivElement | null;
    if (editorContainer) {
      setContainer(editorContainer);
    }
    return () => {
      editorView?.destroy();
    };
  }, [editorView, setContainer]);
  const handleLinting = async () => {
    setLintResults({}); // Clear previous lint results
    setIsLintingDone(false); // Reset linting state
    setIsLinting(true); // Show loading spinner
    if (!editorContent.trim()) {
      setIsLinting(false); // Hide spinner if no content
      return;
    }
    try {
      const { token } = await identity.getCredentials();
      const backendHost = await config.getString('backend.baseUrl');
      const ruleTypes = ['oas_rules', 'custom_rules', 'security_rules'];
      const compressedContent = pako.deflate(editorContent);
      const compressedString = btoa(String.fromCharCode(...compressedContent));
      const lintPromises = ruleTypes.map(ruleType => {
        return fetch(`${backendHost}/api/spectral-plugin-backend/lint`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            content: compressedString,
            ruletype: ruleType,
          }),
        }).then(res =>
          res.json().then(apiData => ({
            ruleType,
            data: apiData?.results?.lint_results?.[ruleType] || [],
          })),
        );
      });
      const lintResultsArray = await Promise.all(lintPromises);
      const newResults: { [key: string]: any[] } = {};
      lintResultsArray.forEach(({ ruleType, data }) => {
        newResults[ruleType] = data;
      });
      setLintResults(newResults);
      setIsLintingDone(true);
    } catch (error) {
      setLintResults({});
    } finally {
      setIsLinting(false); // Hide spinner after linting is complete
    }
  };
  const formatRuleName = (ruleType: string) => {
    const ruleMap: { [key: string]: string } = {
      oas_rules: 'OpenAPI rules',
      custom_rules: 'Principal custom rules',
      security_rules: 'OWASP rules',
    };
    return ruleMap[ruleType] || ruleType;
  };
  // Function to render icons based on linting results
  const renderIcon = (section: string) => {
    if (isLinting) return null; // Do not show icons while linting is in progress
    if (!isLintingDone) return null; // Do not show icons if linting is not done
    return lintResults[section]?.length > 0 ? (
      <ErrorOutlineRoundedIcon style={{ color: 'red' }} />
    ) : (
      <CheckCircleOutlineRoundedIcon style={{ color: 'green' }} />
    );
  };

  // Move conditional content rendering logic here
  let contentArea = null;
  if (isLinting) {
    contentArea = (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100%',
        }}
      >
        <CircularProgress />
        <span style={{ marginLeft: '10px' }}>Linting in progress...</span>
      </div>
    );
  } else if (isLintingDone) {
    contentArea = (
      <>
        <h3
          style={{
            justifyContent: 'space-between',
            marginBottom: '10px',
            marginTop: '-10px',
          }}
        >
          Missing or invalid values
          {lintResults[selectedSection]?.length > 0 && (
            <span className={classes.issueCount} style={{ marginLeft: '10px' }}>
              {lintResults[selectedSection]?.length}
            </span>
          )}
        </h3>

        {lintResults[selectedSection]?.length > 0 ? (
          lintResults[selectedSection].map((result, index) => {
            const { code, severity, message, range, path } = result;

            const severityStyle = severityStyles[severity](isDark);

            const isHandbookRule = handbookRules.includes(code);

            const removeColon = (str: string) => str.replace(/:/g, '');

            const ruleLink = isHandbookRule
              ? `https://portal.developer.principal.com/docs/principalfinancialgroup-emu/component/principal-engineering-handbook/architecture/integrations/api/rest/linter/rulesets/#${removeColon(
                  code,
                )}`
              : `https://meta.stoplight.io/docs/spectral/4dec24461f3af-open-api-rules#${code.toLowerCase()}`;

            const ruleDescription = isHandbookRule
              ? 'View rule in Principal Engineering Handbook'
              : 'View rule in OpenAPI documentation';

            return (
              <div
                key={index}
                style={{
                  backgroundColor: severityStyle.color,

                  borderRadius: '5px',

                  padding: '10px',

                  marginBottom: '10px',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <div style={{ fontSize: '20px', marginRight: '10px' }}>
                    {severityStyle.icon}
                  </div>
                  <div style={{ flex: 1 }}>
                    <strong>{code}</strong>
                    <p style={{ margin: 0 }}>
                      <strong>Found at Line:</strong>
                      <span style={{ marginLeft: '9px' }}>
                        {range?.start?.line + 1}
                      </span>
                    </p>
                    <p style={{ margin: 0 }}>
                      {message}{' '}
                      {path.map((segment: string, idx: number) => (
                        <span key={idx} style={{ marginLeft: '5px' }}>
                          [{segment}]
                        </span>
                      ))}
                    </p>
                    {/* New "View Rule" Link with dynamic URL and description */}
                    <p style={{ margin: '8px 0 0 0' }}>
                      <a
                        href={ruleLink}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{
                          color: '#007bff',
                          fontWeight: 'bold',
                          textDecoration: 'underline',
                        }}
                      >
                        {ruleDescription}
                      </a>
                    </p>
                  </div>
                </div>
              </div>
            );
          })
        ) : (
          <p>No issues found</p>
        )}
      </>
    );
  }

  return (
    <div className={classes.spectralContainer}>
      {/* Header */}
      <div className={classes.headerContainer}>
        <h1>Embedded Spectral Linter</h1>
        <button className={classes.runLinterButton} onClick={handleLinting}>
          Run
        </button>
      </div>

      {/* Editor */}
      <div className={classes.editorContainer}>
        <div id="editor" />
      </div>

      {/* Bottom Section */}
      <div className={classes.bottomContainer}>
        {/* Left Section - Buttons */}
        <div className={classes.sectionSelector}>
          {['oas_rules', 'custom_rules', 'security_rules'].map(section => (
            <button
              key={section}
              className={`${classes.sectionButton} ${
                selectedSection === section ? classes.selectedButton : ''
              }`}
              onClick={() => setSelectedSection(section)}
            >
              <span>{formatRuleName(section)}</span>
              {renderIcon(section)}
            </button>
          ))}
        </div>

        <div className={classes.contentArea}>{contentArea}</div>
      </div>
    </div>
  );
}
export default EmbeddedSpectralLinter;
